"use client";

import { useState } from "react";
import { ContactItemValue, type ContactItem } from "../../item";
import { ContactItemDeliverability } from "../../item/deliverability";
import { EmailComposeMenu } from "@/components/email-compose-menu";
import { Icon } from "@/components/ui/icon";

export function CandidateReviewContactItem({
  contact,
  onFeedback,
  onDelete,
}: {
  contact: ContactItem;
  onFeedback?: (contactId: string, feedback: "up" | "down") => void;
  onDelete?: (contactId: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!contact.value) return;
    navigator.clipboard.writeText(contact.value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="relative bg-contrast/6 border border-border w-full flex flex-col rounded-md overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      <div className="flex w-full items-center gap-1.5 p-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 px-1">
          {contact.type === "email" && contact.value ? (
            <EmailComposeMenu email={contact.value}>
              <button
                type="button"
                title="Write email"
                className="flex min-w-0 cursor-pointer items-center rounded text-left underline-offset-2 transition-colors hover:text-blue-600 hover:underline dark:hover:text-blue-300"
              >
                <ContactItemValue contact={contact} />
              </button>
            </EmailComposeMenu>
          ) : (
            <ContactItemValue contact={contact} />
          )}
          <ContactItemDeliverability
            deliverability={contact.deliverability}
            type={contact.type}
            isValid={contact.isValid}
          />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {onFeedback && !contact.isLocal && (
            <>
              <button
                type="button"
                onClick={() => onFeedback(contact.id, "up")}
                className={`flex size-5 cursor-pointer items-center justify-center rounded transition-colors ${
                  contact.feedback === "up"
                    ? "text-emerald-500"
                    : "text-foreground opacity-40 hover:opacity-80"
                }`}
              >
                <Icon name="IconThumbsUp" size={16} />
              </button>
              <button
                type="button"
                onClick={() => onFeedback(contact.id, "down")}
                className={`flex size-5 cursor-pointer items-center justify-center rounded transition-colors ${
                  contact.feedback === "down"
                    ? "text-destructive"
                    : "text-foreground opacity-40 hover:opacity-80"
                }`}
              >
                <Icon name="IconThumbsDown" size={16} />
              </button>
            </>
          )}
          {contact.value && (
            <button
              type="button"
              onClick={handleCopy}
              className="flex size-5 cursor-pointer items-center justify-center rounded text-foreground opacity-40 transition-colors hover:opacity-80"
            >
              <Icon
                name={copied ? "IconCheckmark1" : "IconSquareBehindSquare4"}
                size={16}
              />
            </button>
          )}
          {contact.isLocal && onDelete && (
            <button
              type="button"
              onClick={() => onDelete(contact.id)}
              className="flex size-5 cursor-pointer items-center justify-center rounded text-foreground opacity-40 transition-colors hover:text-destructive hover:opacity-100"
            >
              <Icon name="IconTrashCanSimple" size={16} />
            </button>
          )}
        </div>
      </div>
      {contact.isLocal && contact.createdAt && (
        <span className="text-muted-foreground flex w-full justify-start text-xs px-3 pb-2">
          {contact.contributedByName
            ? `Added by ${contact.contributedByName} on ${new Date(contact.createdAt).toLocaleDateString()}`
            : contact.source === "ats"
              ? `Imported from ATS on ${new Date(contact.createdAt).toLocaleDateString()}`
              : `Manually added on ${new Date(contact.createdAt).toLocaleDateString()}`}
        </span>
      )}
      <span className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]" />
    </div>
  );
}
